import { useState } from 'react';

import { IconPill } from '@ingka/pill';
import Skeleton from '@ingka/skeleton';
import checkmarkIcon from '@ingka/ssr-icon/paths/checkmark';
import copyIcon from '@ingka/ssr-icon/paths/copy';
import type { AssetPreview } from '@repo/api-types/customer-assets';
import { useStoreDetails } from '@repo/hooks';
import { translate } from '@repo/phrase';
import { Paragraph } from '@repo/ui/typography';
import { formatDateTime, isActionKey } from '@repo/utils';

import { formatPrice } from '@/utils/translation.utils';
import { getAppConfig } from '@/utils/window.utils';

import styles from './styles.module.scss';

export const AssetRow = ({
    asset,
    assetIndex,
    storeDetailsBaseUrl,
    viewDetail,
}: {
    asset: AssetPreview;
    assetIndex: number;
    storeDetailsBaseUrl: string;
    viewDetail: (type: string, refId: string) => void;
}) => {
    const { data: storeDetails, isPending } = useStoreDetails(storeDetailsBaseUrl, asset.buCode);
    const storeTimezone = storeDetails?.TimeZone;
    const { dateTimeFormat } = getAppConfig();
    const assetDate =
        storeTimezone !== undefined
            ? formatDateTime(asset.creationDate, dateTimeFormat, storeTimezone)
            : formatDateTime(asset.creationDate, dateTimeFormat);
    const assetAlias = asset.alias;
    const assetType = asset.assetType;
    const assetId = asset.refId;
    const [isCopied, setIsCopied] = useState(false);
    const assetStatus = asset.status;
    const assetValue = asset.value;
    const assetCurrency = asset.currencyCode;

    return (
        <div
            key={assetId}
            className={styles.assets_table_row}
            data-testid={`assets-table-row-${assetIndex}`}
            onClick={() => {
                const selection = globalThis.getSelection();
                if (selection && selection.toString().length > 0) {
                    return;
                }
                viewDetail(assetType, assetId);
            }}
            onKeyDown={(e) => {
                if (isActionKey(e.key)) {
                    viewDetail(assetType, assetId);
                }
            }}
            // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
            role="button"
            tabIndex={0}
        >
            <div className={styles.asset_date}>
                {isPending ? <Skeleton data-testid="asset-row-skeleton" /> : <Paragraph>{assetDate}</Paragraph>}
                {/* TODO: ASOT-6132 */}
            </div>
            <div className={styles.asset_alias}>
                <Paragraph>{assetAlias}</Paragraph>
            </div>
            <div className={styles.asset_type}>
                <Paragraph>{translate(`filter_type_${assetType}`)}</Paragraph>
            </div>
            <div className={styles.asset_id}>
                <Paragraph>{assetId}</Paragraph>
                <span
                    role="presentation"
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                    onKeyDown={(e) => {
                        e.stopPropagation();
                    }}
                >
                    <IconPill
                        ariaLabel={translate('asset_list_table_copy_asset_id')}
                        ssrIcon={isCopied ? checkmarkIcon : copyIcon}
                        size="xsmall"
                        onClick={(e) => {
                            void navigator.clipboard.writeText(assetId);
                            e.currentTarget.blur();
                            setIsCopied(true);
                            setTimeout(() => {
                                setIsCopied(false);
                            }, 2000);
                        }}
                    />
                </span>
            </div>
            <div className={styles.asset_status}>
                <Paragraph>{translate(`asset_list_table_status_${assetStatus.toUpperCase()}`)}</Paragraph>
            </div>
            <div className={styles.asset_value}>
                <Paragraph>{formatPrice(assetValue, assetCurrency)}</Paragraph>
            </div>
        </div>
    );
};
